import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Pagination,
  SelectChangeEvent,
} from "@mui/material";
import { LuSearch, LuPlus } from "react-icons/lu";
import { OFF_WHITE, muiFieldSx, filterFieldSx, DARK_NAVY, DARK_NAVY_LIGHT, INPUT_BORDER_FOCUS } from "../../constants/layout";
import RegisterForm from "../AddUserTable/AddUser";

const BLACK = "#000000";
const ICON_BLUE = INPUT_BORDER_FOCUS;
const CARD_SHADOW = "0 2px 16px rgba(0, 0, 0, 0.06)";
const PROFILE_FONT = '"Inter", sans-serif';
const BORDER = "#E8E8E8";
const fieldSx = muiFieldSx;

const cardSx = {
  bgcolor: "#ffffff",
  borderRadius: "16px",
  boxShadow: CARD_SHADOW,
  border: "1px solid rgba(0,0,0,0.04)",
};

const STATUS_CONFIG: Record<number, { label: string; bg: string; color: string }> = {
  1: { label: "Incomplete", bg: "#FFF3E8", color: "#E07A2F" },
  2: { label: "Under Review", bg: "#EEF2F8", color: ICON_BLUE },
  3: { label: "NSE KYC", bg: "#E8F8EE", color: "#08991F" },
  4: { label: "No NSE KYC", bg: "#FDECEC", color: "#D32F2F" },
};

interface User {
  id: number;
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  mobile: string;
  logInBy: number;
  onboardStatus: number;
  createdAt: string;
  aumUserId: string;
}

interface UserTableProps {
  users: User[];
  editUser: User | null;
  onEdit: (user: User) => void;
  onSave: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCancel: () => void;
  totalPages: number;
  pageSize: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  onPageSizeChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  currentPage: number;
  sortBy: number;
  onboardStatus: number;
  startDate: string;
  endDate: string;
  onChangeSortBy: (sortBy: number) => void;
  onChangeOnboardStatus: (oStatus: number) => void;
  onChangeStartDate: (sDate: string) => void;
  onChangeEndDate: (eDate: string) => void;
  onRefresh?: () => void;
}

const StatusPill = ({ status }: { status: number }) => {
  const config = STATUS_CONFIG[status];
  if (!config) return null;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.5,
        borderRadius: "999px",
        fontSize: 12,
        fontWeight: 600,
        fontFamily: PROFILE_FONT,
        bgcolor: config.bg,
        color: config.color,
        whiteSpace: "nowrap",
      }}
    >
      {config.label}
    </Box>
  );
};

const UserAvatar = ({ name }: { name: string }) => {
  const initial = (name?.trim()?.[0] || "I").toUpperCase();
  return (
    <Avatar
      sx={{
        width: 36,
        height: 36,
        bgcolor: ICON_BLUE,
        fontSize: 14,
        fontWeight: 700,
        fontFamily: PROFILE_FONT,
      }}
    >
      {initial}
    </Avatar>
  );
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const UserTable: React.FC<UserTableProps> = ({
  users = [],
  totalPages,
  currentPage,
  pageSize,
  sortBy,
  onboardStatus,
  onChangeSortBy,
  onChangeOnboardStatus,
  onPageChange,
  onPageSizeChange,
  onRefresh,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [addInvestorOpen, setAddInvestorOpen] = useState(false);

  const filteredUsers = Array.isArray(users)
    ? users.filter(
        (user) =>
          user.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.mobile.includes(searchQuery) ||
          user.aumUserId?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const totalRecords = totalPages * pageSize;
  const from = users.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = users.length === 0 ? 0 : (currentPage - 1) * pageSize + users.length;

  const headCellSx = {
    fontFamily: PROFILE_FONT,
    fontSize: 12,
    fontWeight: 600,
    color: DARK_NAVY_LIGHT,
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    borderBottom: `1px solid ${BORDER}`,
    py: 1.75,
    bgcolor: "#FAFAFA",
  };

  const bodyCellSx = {
    fontFamily: PROFILE_FONT,
    fontSize: 14,
    color: DARK_NAVY,
    borderBottom: `1px solid #F5F5F5`,
    py: 1.75,
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: OFF_WHITE,
        px: { xs: 3, sm: 5, md: 6, lg: 8 },
        py: { xs: 3, md: 4 },
        fontFamily: PROFILE_FONT,
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: { xs: 24, md: 28 },
                fontWeight: 700,
                color: DARK_NAVY,
                mb: 0.5,
              }}
            >
              Investor Management
            </Typography>
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
              Manage and track all investor onboarding and profiles.
            </Typography>
          </Box>
          <Button
            onClick={() => setAddInvestorOpen(true)}
            startIcon={<LuPlus size={18} />}
            sx={{
              fontFamily: PROFILE_FONT,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              bgcolor: ICON_BLUE,
              color: "#ffffff",
              borderRadius: "10px",
              px: 2.5,
              py: 1.25,
              boxShadow: "none",
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: "#16304f", boxShadow: "none" },
            }}
          >
            Add Investor
          </Button>
        </Box>

        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: { xs: "stretch", md: "center" },
            }}
          >
            <TextField
              placeholder="Search investors by name, email or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              variant="outlined"
              sx={{ ...filterFieldSx, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: "flex", alignItems: "center", pl: 0.5, pr: 1, color: DARK_NAVY_LIGHT }}>
                    <LuSearch size={18} />
                  </Box>
                ),
              }}
            />

            <FormControl size="small" sx={{ ...filterFieldSx, minWidth: { xs: "100%", md: 200 } }}>
              <InputLabel id="sort-label">Sort By</InputLabel>
              <Select
                labelId="sort-label"
                value={sortBy}
                onChange={(e: SelectChangeEvent<number>) => onChangeSortBy(Number(e.target.value))}
                label="Sort By"
              >
                <MenuItem value={1}>Name (A-Z)</MenuItem>
                <MenuItem value={2}>Name (Z-A)</MenuItem>
                <MenuItem value={3}>Date (Oldest First)</MenuItem>
                <MenuItem value={4}>Date (Newest First)</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ ...filterFieldSx, minWidth: { xs: "100%", md: 200 } }}>
              <InputLabel id="onboard-status-label">Onboard Status</InputLabel>
              <Select
                labelId="onboard-status-label"
                value={onboardStatus}
                onChange={(e: SelectChangeEvent<number>) => onChangeOnboardStatus(Number(e.target.value))}
                label="Onboard Status"
              >
                <MenuItem value={-1}>All Status</MenuItem>
                <MenuItem value={1}>Incomplete</MenuItem>
                <MenuItem value={2}>Under Review</MenuItem>
                <MenuItem value={3}>NSE KYC</MenuItem>
                <MenuItem value={4}>No NSE KYC</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box sx={{ ...cardSx, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Investor</TableCell>
                  <TableCell sx={headCellSx}>User ID</TableCell>
                  <TableCell sx={headCellSx}>Email</TableCell>
                  <TableCell sx={headCellSx}>Mobile</TableCell>
                  <TableCell sx={headCellSx}>Onboarding Status</TableCell>
                  <TableCell sx={headCellSx}>Registered At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ ...bodyCellSx, textAlign: "center", py: 6, borderBottom: "none" }}>
                      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
                        No investors found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => {
                    const fullName = `${user.firstName} ${user.lastName || ""}`.trim();
                    return (
                      <TableRow
                        key={user.id}
                        hover
                        sx={{ "&:hover": { bgcolor: "#FAFAFA" }, "&:last-child td": { borderBottom: "none" } }}
                      >
                        <TableCell sx={bodyCellSx}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <UserAvatar name={fullName} />
                            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, fontWeight: 600, color: BLACK }}>
                              {fullName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }}>{user.aumUserId}</TableCell>
                        <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }}>{user.email}</TableCell>
                        <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }}>{user.mobile}</TableCell>
                        <TableCell sx={bodyCellSx}>
                          <StatusPill status={user.onboardStatus} />
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }}>
                          {formatDate(user.createdAt)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
              gap: 2,
              px: { xs: 2, md: 2.5 },
              py: 2,
              borderTop: `1px solid ${BORDER}`,
            }}
          >
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY_LIGHT }}>
              Showing {from} to {to} of {totalRecords} investors
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ ...fieldSx, minWidth: 130 }}>
                <Select
                  value={pageSize}
                  onChange={(e) =>
                    onPageSizeChange(e as React.ChangeEvent<HTMLInputElement>)
                  }
                  displayEmpty
                >
                  <MenuItem value={10}>10 per page</MenuItem>
                  <MenuItem value={20}>20 per page</MenuItem>
                  <MenuItem value={50}>50 per page</MenuItem>
                  <MenuItem value={100}>100 per page</MenuItem>
                </Select>
              </FormControl>

              <Pagination
                count={totalPages || 1}
                page={currentPage}
                onChange={(_, page) => onPageChange(null, page - 1)}
                shape="rounded"
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontFamily: PROFILE_FONT,
                    fontSize: 13,
                    color: DARK_NAVY_LIGHT,
                    borderRadius: "8px",
                  },
                  "& .Mui-selected": {
                    bgcolor: `${ICON_BLUE} !important`,
                    color: "#ffffff !important",
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <RegisterForm
        open={addInvestorOpen}
        onClose={() => setAddInvestorOpen(false)}
        onSuccess={onRefresh}
      />
    </Box>
  );
};

export default UserTable;
